import { type CSSProperties } from 'react';
import { getPageCatalogEntry } from '@classy/shared';
import { Modal } from '../components/Modal/Modal.js';
import { CLASSROOM_PAGE_MODULES, type ClassroomPageKey } from './registry.js';
import styles from './PageStore.module.css';

// Cap the catalog grid columns; the dialog hugs this many so it stays a
// sensible width with one item or a larger future catalog.
const MAX_CATALOG_COLS = 4;

const PAGE_BLURBS: Record<string, string> = {
  home: 'Greeting and message-of-the-day card.',
  calendar: 'Monthly grid with events + birthdays.',
  'day-of-school': 'Hundreds-chart day counter.',
  weather: 'Live conditions for the classroom.',
  feelings: 'Daily feelings check-in.',
  question: 'Question of the day with answers.',
  pledges: 'Classroom pledges said each morning.',
  number: 'Place value, currency, and number neighbors.',
  youtube: 'A lesson playlist of embedded YouTube videos.',
};

const PAGE_GLYPHS: Record<string, string> = {
  home: '🏠',
  calendar: '📅',
  'day-of-school': '🔢',
  weather: '☀️',
  feelings: '💛',
  question: '❓',
  pledges: '🙋',
  number: '✏️',
  youtube: '▶',
};

export interface PageStoreProps {
  open: boolean;
  onClose: () => void;
  /** Pages already added to the classroom; filtered out of the catalog. */
  enabled: readonly ClassroomPageKey[];
  /** Add a not-yet-added page to the classroom. */
  onAdd: (key: ClassroomPageKey) => void;
}

/**
 * Browseable catalog of classroom-page modules NOT yet added to this
 * classroom. Removing pages happens on the General tab itself; this
 * modal is purely an "add pages you don't have yet" surface so it
 * stays focused as the catalog grows.
 */
export function PageStore({ open, onClose, enabled, onAdd }: PageStoreProps) {
  const enabledSet = new Set<string>(enabled);
  // Only Morning Meeting pages are added/removed here. Home + standalone
  // class-level pages (YouTube, Links) are permanent and toggled on the
  // Classroom Pages strip instead.
  const available = CLASSROOM_PAGE_MODULES.filter(
    (m) => !enabledSet.has(m.key) && getPageCatalogEntry(m.key)?.placement === 'morning-meeting',
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Page store"
      description="Add a page from the catalog. Manage order + remove on the General tab. More pages coming soon."
      testId="page-store"
      size="fit"
    >
      <div className={styles.body}>
        {available.length === 0 ? (
          <p className={styles.empty}>
            All available pages are already added to this classroom — nothing more to add for now.
          </p>
        ) : (
          <>
            <p className={styles.hint}>
              {available.length} page{available.length === 1 ? '' : 's'} available to add.
            </p>
            <div
              className={styles.grid}
              style={{ ['--cols']: Math.min(available.length, MAX_CATALOG_COLS) } as CSSProperties}
              role="group"
              aria-label="Available pages"
            >
              {available.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => onAdd(m.key as ClassroomPageKey)}
                  className={styles.tile}
                  data-testid={`page-store-tile-${m.key}`}
                >
                  <div className={styles.row}>
                    <span className={styles.glyph} aria-hidden="true">
                      {PAGE_GLYPHS[m.key] ?? '✦'}
                    </span>
                    <span className={styles.label}>{m.label}</span>
                  </div>
                  <p className={styles.blurb}>{PAGE_BLURBS[m.key] ?? ''}</p>
                  <span className={styles.statePill}>+ Add</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
